import { Loading } from "@vellum/ui";
import { useEffect, useState } from "react";

import { vellumApiSdk } from "@vellum/micro-app-auth";
import { ensureMetabaseEmbedLoaded } from "@/lib/metabase";

type MetabaseReportProps = {
  /** Metabase question/report id to embed (e.g. METABASE_QUESTION_ID). */
  metabaseReportId: number;
  /**
   * Editable filter defaults, keyed by the question's SQL variable / parameter slug
   * (e.g. { company, facility, from_date_filter, to_date_filter }). For a guest embed
   * these are the only way to pre-fill filters; the user can still change them.
   */
  sqlParameters: Record<string, string>;
  /**
   * Parameter slugs whose filter widgets are hidden in the embed. Paired with a value
   * in `sqlParameters`, this locks the filter: the value applies but can't be changed.
   */
  hiddenParameters?: string[];
};

/**
 * Metabase guest embed of a native SQL question. Grows to fill its parent, so
 * mount it in a flex column that has a height to give.
 */
export function MetabaseReport({
  metabaseReportId,
  sqlParameters,
  hiddenParameters,
}: MetabaseReportProps) {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ensureMetabaseEmbedLoaded();
    let active = true;
    setToken(null);
    setError(null);
    vellumApiSdk
      .createMetabaseGuestToken({ body: { question_id: metabaseReportId } })
      .then((res) => {
        if (active) setToken(res.data?.token ?? "");
      })
      .catch(() => {
        if (active) setError("Could not load the report.");
      });
    return () => {
      active = false;
    };
  }, [metabaseReportId]);

  if (error) {
    return <p className="p-4 text-sm text-error">{error}</p>;
  }

  if (!token) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loading />
      </div>
    );
  }

  // Kebab-case attributes are set as element attributes (JSON strings), which the
  // guest embed reads on load. `initial-sql-parameters` handles native SQL variables;
  // `initial-parameters` is included as a fallback for GUI-style parameters.
  const paramsJson = JSON.stringify(sqlParameters);

  return (
    <metabase-question
      question-id={metabaseReportId}
      token={token}
      initial-sql-parameters={paramsJson}
      initial-parameters={paramsJson}
      hidden-parameters={
        hiddenParameters?.length ? JSON.stringify(hiddenParameters) : undefined
      }
      with-title="false"
      with-downloads="true"
      style={{
        flex: 1,
        // Without this the embed's own scroll area would push the flex item
        // past the height the page gave it instead of scrolling inside it.
        minHeight: 0,
        border: "none",
      }}
    />
  );
}
